/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import TQInitParams from "../../public/TQInitParam";
import { TQLogLevel } from "../../public/TQTypes";
// import greenlet from "greenlet";

export const DOC_FOLDER_EMFS = "/persistent/fonts/";
export const DOC_FOLDER_MEMFS = "/fonts/";
const EstimateEnabled = navigator.storage != undefined && navigator.storage.estimate != undefined;
let OPFSEnabled = navigator.storage != undefined;

declare global {
    interface FileSystemFileHandle {
        createSyncAccessHandle: any;
        close: any;
    }

    interface FileSystemGetFileOptions {
        create?: boolean;
    }

    interface FileSystemRemoveOptions {
        recursive?: boolean;
    }

    interface FileSystemGetDirectoryOptions {
        create?: boolean;
    }

    interface FileSystemDirectoryHandle {
        entries(): any;
        getFileHandle(name: string, options?: FileSystemGetFileOptions | undefined): Promise<FileSystemFileHandle>;
        removeEntry(name: string, options?: FileSystemRemoveOptions | undefined): Promise<void>;
        getDirectoryHandle(name: string, options?: FileSystemGetDirectoryOptions | undefined): Promise<FileSystemDirectoryHandle>;
    }

    interface StorageManager {
        getDirectory(): Promise<FileSystemDirectoryHandle>;
    }
}

export interface EntryItem {
    fileName: string;
    fileSize: number; // size in bytes.
}

export namespace FileSystem {
    /*
    function createSyncAccessHandle(file: FileSystemFileHandle) {
        // FIXME: we can remove this test once the mode param is required
        if (Object.getPrototypeOf(file).createSyncAccessHandle.length === 0) {
            return file.createSyncAccessHandle();
        }
        return file.createSyncAccessHandle({ mode: "in-place" });
    }
    */

    async function detectOPFSEnabled() {
        if (OPFSEnabled) {
            try {
                const root = await navigator.storage.getDirectory();
                const file = await root.getFileHandle("access-handle-detect", { create: true });
                await root.removeEntry("access-handle-detect");
            } catch (e) {
                TQInitParams.Logger.logMessage(TQLogLevel.INFO, `Attempt to use OPFS fails, falling back to MEMFS`);
                OPFSEnabled = false;
            }
        }
        return OPFSEnabled;
    }

    export function isOPFSEnabled() {
        return OPFSEnabled;
    }

    export function isEstimateEnabled() {
        return EstimateEnabled;
    }

    export async function getAvailableStorage(): Promise<number> {
        const result = await detectOPFSEnabled();
        try {
            if (navigator.storage && navigator.storage.estimate) {
                const quota = await navigator.storage.estimate();
                // quota.usage -> Number of bytes used.
                // quota.quota -> Maximum number of bytes available.
                if (quota.quota && quota.usage) {
                    const remaining = quota.quota - quota.usage;
                    return remaining;
                }
            }
        } catch (e) {
            TQInitParams.Logger.logMessage(TQLogLevel.INFO, `getAvailableStorage error: ${e}`);
            return 0;
        }
        return 0;
    }

    export async function EnumerateDir(dirPath: string) {
        if (isOPFSEnabled()) {
            // attempt to use OPFS
            try {
                const root = await navigator.storage.getDirectory();
                const parts = dirPath.split("/").filter(i => i !== "");
                const directories: EntryItem[] = [];
                for await (const [name, handle] of root.entries()) {
                    if (name === parts[parts.length - 1]) {
                        for await (const entry of handle.values()) {
                            if (entry.kind == "file") {
                                const file = await entry.getFile();
                                const newItem: EntryItem = { fileName: file.name, fileSize: file.size };
                                directories.push(newItem);
                            }
                        }
                    }
                }
                return directories;
            } catch (e) {
                TQInitParams.Logger.logMessage(TQLogLevel.INFO, `OPFS EnumerateDir error: ${e}`);
            }
        }
        // enumerate from MEMFS if OPFS fails
        try {
            return FS.readdir(dirPath);
        } catch (e) {
            TQInitParams.Logger.logMessage(TQLogLevel.INFO, `MEMFS EnumerateDir error: ${e}`);
            return [];
        }
    }

    export async function RemoveFile(dirPath: string, filePath: string): Promise<boolean> {
        if (isOPFSEnabled()) {
            // attempt to use OPFS
            try {
                const root = await navigator.storage.getDirectory();
                // read from DOC_FOLDER_EMFS, which should be of form /persistent/fonts
                const parts = dirPath.split("/").filter(i => i !== "");
                for await (const [name, handle] of root.entries()) {
                    if (name === parts[parts.length - 1]) {
                        await handle.removeEntry(filePath, { recursive: true });
                        return true;
                    }
                }
            } catch (e) {
                TQInitParams.Logger.logMessage(TQLogLevel.INFO, `OPFS RemoveFile error:: ${e}`);
            }
        }
        // remove from MEMFS if OPFS fails
        try {
            const tmpFilename = DOC_FOLDER_MEMFS + filePath;
            FS.unlink(tmpFilename);
            return true;
        } catch (e) {
            TQInitParams.Logger.logMessage(TQLogLevel.INFO, `MEMFS RemoveFile error:: ${e}`);
            return false;
        }
    }

    export async function FileExists(dirPath: string, path: string): Promise<boolean> {
        if (isOPFSEnabled()) {
            // attempt to use OPFS
            try {
                const root = await navigator.storage.getDirectory();
                // write to DOC_FOLDER_EMFS, which should be of form /persistent/fonts
                const parts = dirPath.split("/").filter(i => i !== "");
                for await (const [name, handle] of root.entries()) {
                    if (name === parts[parts.length - 1]) {
                        const file = await handle.getFileHandle(path, { create: false });
                        if (file) return true;
                    }
                }
            } catch (e) {
                TQInitParams.Logger.logMessage(TQLogLevel.INFO, `OPFS FileExists error: ${e}`);
            }
        }
        // write to MEMFS if OPFS fails; could be if two tabs attempting write to same filename
        const tmpFilename = DOC_FOLDER_MEMFS + path;
        try {
            FS.stat(tmpFilename);
            return true;
        } catch (e) {
            return false;
        }
    }

    /*
    let SyncWriteFile = greenlet(
        async (dirPath: string, fileName: string, fileData: Uint8Array, fileSize: number): Promise<boolean> => {
            if (isDebug) TQInitParams.Logger.logMessage(TQLogLevel.DEBUG, `OPFSWorker.writeFile: ${dirPath} ${fileName} ${fileData}`);
            try {
                const root = await navigator.storage.getDirectory();
                // write to DOC_FOLDER_EMFS, which should be of form /persistent/fonts
                const parts = dirPath.split("/").filter(i => i !== "");
                let tmpHandle;
                const iter = root.values();
                let result = await iter.next();
                while (!result.done) {
                    const h = result.value;
                    if (h.name === parts[parts.length - 1]) {
                        tmpHandle = h;
                    }
                    result = await iter.next();
                }
                // if "fonts" directory not yet created by pthreadfs, then created it now
                if (!tmpHandle) {
                    tmpHandle = await root.getDirectoryHandle(parts[parts.length-1], { create: true });
                }
                if (tmpHandle) {
                    const file = await tmpHandle.getFileHandle(fileName, {
                        create: true,
                    });
                    const accessHandle = await createSyncAccessHandle(file);
                    accessHandle.write(fileData, { at: 0 });
                    await accessHandle.close();
                    if (isDebug) TQInitParams.Logger.logMessage(TQLogLevel.DEBUG, `OPFSWorker.writeFile to ${dirPath} ${fileName}`);
                    return true;
                }
            } catch (e) {
                if (isDebug) TQInitParams.Logger.logMessage(TQLogLevel.DEBUG, `OPFSWorker.writeFile failed ${e}`);
            }
            if (isDebug) TQInitParams.Logger.logMessage(TQLogLevel.DEBUG, `OPFSWorker.writeFile failed`);
            throw new Error(`Unable to perform write operation on "${fileName}"`);
        }
    );
    */

    async function AsyncWriteFile(dirPath: string, fileName: string, fileData: Uint8Array, fileSize: number) {
        const root = await navigator.storage.getDirectory();
        // write to DOC_FOLDER_EMFS, which should be of form /persistent/fonts
        const parts = dirPath.split("/").filter(i => i !== "");
        let tmpHandle;
        for await (const [name, handle] of root.entries()) {
            if (name === parts[parts.length - 1]) {
                tmpHandle = handle;
            }
        }
        // if "fonts" directory not yet created by pthreadfs, then created it now
        if (!tmpHandle) {
            tmpHandle = await root.getDirectoryHandle(parts[parts.length - 1], { create: true });
        }
        if (tmpHandle) {
            const file = await tmpHandle.getFileHandle(fileName, {
                create: true,
            });
            const writable = await file.createWritable();
            await writable.write(fileData);
            await writable.close();
            return true;
        }

        return false;
    }

    export async function WriteFile(dirPath: string, fileName: string, fileData: Uint8Array, fileSize: number): Promise<boolean> {
        if (isOPFSEnabled()) {
            try {
                const time = performance.now();
                const success = await AsyncWriteFile(dirPath, fileName, fileData, fileSize);
                TQInitParams.Logger.logMessage(TQLogLevel.DEBUG, `OPFSWriteFile operations done. Took ${Math.round(performance.now() - time)} milliseconds.`);
                return success;
            } catch (e) {
                TQInitParams.Logger.logMessage(TQLogLevel.INFO, `OPFS WriteFile error: ${e}`);
            }
        }
        // write to MEMFS if OPFS fails; could be if two tabs attempting write to same filename
        const tmpFilename = DOC_FOLDER_MEMFS + fileName;
        try {
            FS.writeFile(tmpFilename, new Uint8Array(fileData), {
                // @ts-expect-error not yet implemented in types
                encoding: "binary",
                canOwn: true,
                flags: "w+",
            });
            return true;
        } catch (e) {
            TQInitParams.Logger.logMessage(TQLogLevel.INFO, `MEMFS WriteFile error: ${e}`);
            return false;
        }
    }

    /*
    let SyncReadFile = greenlet(
        async (dirPath: string, fileName: string): Promise<Uint8Array> => {
            if (isDebug) TQInitParams.Logger.logMessage(TQLogLevel.DEBUG, `OPFSWorker.readFile ${dirPath} ${fileName}`);
            try {
                const root = await navigator.storage.getDirectory();
                // read from path, which should be of form /persistent/tmp
                const parts = dirPath.split("/").filter(i => i !== "");
                const iter = root.values();
                let result = await iter.next();
                while (!result.done) {
                    const h = result.value;
                    if (h.name === parts[parts.length - 1]) {
                        const file = await h.getFileHandle(fileName, { create: false });
                        const accessHandle = await file.createSyncAccessHandle();
                        // Get size of the file.
                        const fileSize = await accessHandle.getSize();
                        // Read file content to a buffer.
                        const readBuffer = new Uint8Array(fileSize);
                        const readSize = accessHandle.read(readBuffer, { at: 0 });
                        await accessHandle.close();
                        if (isDebug) TQInitParams.Logger.logMessage(TQLogLevel.DEBUG, `OPFSWorker.readFile ${dirPath} ${fileName} readBytes ${readSize}`);
                        return readBuffer;
                    }
                    result = await iter.next();
                }
            } catch (e) {
                TQInitParams.Logger.logMessage(TQLogLevel.DEBUG, `OPFSWorker.readFile error ${e}`);
            }
            if (isDebug) TQInitParams.Logger.logMessage(TQLogLevel.DEBUG, `OPFSWorker.readFile failed`);
            throw new Error(`Unable to perform read operation on "${fileName}"`);
        }
    );
    */

    async function AsyncReadFile(dirPath: string, fileName: string) {
        const root = await navigator.storage.getDirectory();
        // read from path, which should be of form /persistent/fonts
        const parts = dirPath.split("/").filter(i => i !== "");
        for await (const [name, handle] of root.entries()) {
            if (name === parts[parts.length - 1]) {
                const file = await handle.getFileHandle(fileName, { create: false });
                const fh = await file.getFile();
                const data = await fh.arrayBuffer();
                return data;
            }
        }
    }

    export async function ReadFile(dirPath: string, fileName: string): Promise<Uint8Array> {
        if (isOPFSEnabled()) {
            try {
                const time = performance.now();
                const data = await AsyncReadFile(DOC_FOLDER_EMFS, fileName);
                TQInitParams.Logger.logMessage(TQLogLevel.DEBUG, `OPFSReadFile operations done. Took ${Math.round(performance.now() - time)} milliseconds.`);
                return data;
            } catch (e) {
                TQInitParams.Logger.logMessage(TQLogLevel.INFO, `OPFS ReadFile error: ${e}`);
            }
        }
        // read from MEMFS if OPFS fails; perhaps two tabs exporting file at same time?
        const tmpFilename = DOC_FOLDER_MEMFS + fileName;
        try {
            const data = FS.readFile(tmpFilename, { encoding: "binary" });
            return data;
        } catch (e) {
            TQInitParams.Logger.logMessage(TQLogLevel.INFO, `MEMFS ReadFile error: ${e}`);
            return new Uint8Array();
        }
    }
}
